import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import createUrl from "../../utilities/createUrl/index.js";
import createTransformationQuery from "../../utilities/createTransformationQuery/index.js";
import { useImagePreloadRenderer } from "../ImagePreloadExtractor/index.js";
import { createNonExclusiveMediaQuery } from "../../utilities/createMediaQueriesFromProps/index.js";
import intrinsicDimensionsFromProps from "../../utilities/intrinsicDimensionsFromProps/index.js";
import transformationsShape from "../transformationsShape.js";
/**
 * Render a source element. Can only be used as a child to `Picture`
 */

const Source = props => {
  const src = createUrl(props);
  const {
    // Not adding these props to prop types because
    // they should not be documented as props available to consumers
    // Instead these props should come from the parent Picture
    // eslint-disable-next-line react/prop-types
    renderPreload: givenRenderer,
    // eslint-disable-next-line react/prop-types
    preload,
    // eslint-disable-next-line react/prop-types
    lazy,
    // eslint-disable-next-line react/prop-types
    __createQuery = createNonExclusiveMediaQuery,
    pixelDensity,
    viewport,
    elementProps
  } = props;
  const {
    width,
    height
  } = intrinsicDimensionsFromProps(props);

  const media = __createQuery({
    viewport,
    pixelDensity
  });

  const srcsetProp = lazy ? 'data-srcset' : 'srcSet';
  const fullSrcSet = `${src}${createTransformationQuery(props)}`;
  const sourceProps = {
    [srcsetProp]: fullSrcSet
  };
  const renderPreload = useImagePreloadRenderer(givenRenderer);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("source", _extends({}, sourceProps, {
    width: width,
    height: height
  }, elementProps, {
    media: media
  })), preload && renderPreload({
    rel: 'preload',
    as: 'image',
    media,
    href: fullSrcSet
  }));
};
/**
 * Allow Picture component to do special processing of this element
 */
// eslint-disable-next-line no-underscore-dangle


Source.__isSourceNode = true;
Source.defaultProps = {
  viewport: 'xs'
};
process.env.NODE_ENV !== "production" ? Source.propTypes = {
  /**
   * Set the desired displayed width of the image
   */
  width: PropTypes.number,

  /**
   * Set the desired displayed height of the image
   */
  height: PropTypes.number,

  /**
   * Override the pixel density provided by the parent `Picture` component
   *
   * As noted above, this is used to enhance the size of the image.
   */
  pixelDensity: PropTypes.number,

  /**
   * Viewport size to activate this image (Only supported by Source component)
   */
  viewport: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),

  /**
   * Props to pass to `source` or `img` element
   */
  elementProps: PropTypes.shape({}),

  /**
   * A 4 element tuple with the crop coordinates (`[x1, y1, x2, y2]`)
   * to pass to the media API. [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  crop: PropTypes.arrayOf(PropTypes.number),

  /**
   * Enable smartCrop as the sizing strategy. [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  smartCrop: PropTypes.bool,

  /**
   * Force image to fit in given dimensions (Only makes sense when height and width are provided).
   *
   * [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  fit: PropTypes.bool,

  /**
   * Number of degrees to rotate the image
   *
   * [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api)
   */
  rotate: PropTypes.number,

  /**
   * Set the quality parameter to all images. [See Media API's documentation](https://api-docs.eng.xogrp.com/details/production-media-api).
   */
  quality: PropTypes.string,

  /**
   * Override transformations. If array is provided, transformations are applied in the given order. Transformations shape includes
   *
   * | name | type |
   * |------|------|
   * | width | `number` |
   * | height | `number` |
   * | rotate | `number` |
   * | crop | `[x1: number, y1: number, x2: number, y2: number]` |
   * | fit | `bool` |
   * | smartCrop | `bool` |
   */
  transformations: PropTypes.oneOfType([transformationsShape, PropTypes.arrayOf(transformationsShape)])
} : void 0;
export default Source;